import { Constructor } from '@angular/cdk/table';
import {
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { compareArrays } from '../utils/array-utils';
import { Subject } from 'rxjs';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { FORM_FIELDS } from 'src/app/article-content/components/article-content-form/constants/form-fields';

type WithContentBodyFormSectionType = {
  _initContentBody(
    body: string,
    imgs: IImgMediaDto[],
    videos: IVideoMediaDto[]
  ): void;
  _initContentBodyValueChanges(
    getPreviousBodyValue: () => string | null,
    getPreviousImagesValue: () => IImgMediaDto[] | null,
    getPreviousVideosValue: () => IVideoMediaDto[] | null
  ): void;
  _buildBody(): string;
  _buildBodyImages(): IImgMediaDto[];
  _buildBodyVideos(): IVideoMediaDto[];
  _initNoImgValidation(): ValidatorFn;
  get body(): string;
  get images(): IImgMediaDto[];
  get videos(): IVideoMediaDto[];
};

type ContentBodyFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    BODY: () => string;
    IMGS: () => string;
    VIDEOS: () => string;
    CONTENT_BODY: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithContentBodyFormSection<
  TBase extends Constructor<ContentBodyFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithContentBodyFormSectionType;
  prototype: WithContentBodyFormSectionType;
} & TBase {
  return class WithContentBodyFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initContentBody(
      body: string,
      imgs: IImgMediaDto[],
      videos: IVideoMediaDto[]
    ): void {
      this.form.get(this.FORM_PATHS.BODY()).setValue(body);

      if (imgs) {
        const imgsControl = this.form.get(this.FORM_PATHS.IMGS());
        imgs.forEach((i) => {
          (imgsControl as FormArray).push(new FormControl(i));
        });
      }

      if (videos) {
        const videosControl = this.form.get(this.FORM_PATHS.VIDEOS());
        videos.forEach((i) =>
          (videosControl as FormArray).push(new FormControl(i))
        );
      }
    }

    _initContentBodyValueChanges(
      getPreviousBodyValue: () => string | null,
      getPreviousImagesValue: () => IImgMediaDto[] | null,
      getPreviousVideosValue: () => IVideoMediaDto[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.BODY())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (newValue === getPreviousBodyValue()) {
              this.form.get(this.FORM_PATHS.BODY()).markAsPristine();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.IMGS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousImagesValue())) {
              this.form.get(this.FORM_PATHS.IMGS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.IMGS()).markAsDirty();
            }
          },
        });

      this.form
        .get(this.FORM_PATHS.VIDEOS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousVideosValue())) {
              this.form.get(this.FORM_PATHS.VIDEOS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.VIDEOS()).markAsDirty();
            }
          },
        });
    }

    _buildBody(): string {
      return this.form.get(this.FORM_PATHS.BODY()).value;
    }

    _buildBodyImages(): IImgMediaDto[] {
      return this.form.get(this.FORM_PATHS.IMGS()).value;
    }

    _buildBodyVideos(): IVideoMediaDto[] {
      return this.form.get(this.FORM_PATHS.VIDEOS()).value;
    }

    get body(): string {
      return this.form.get(this.FORM_PATHS.BODY()).value;
    }

    get images(): IImgMediaDto[] {
      return this.form.get(this.FORM_PATHS.IMGS()).value;
    }

    get videos(): IVideoMediaDto[] {
      return this.form.get(this.FORM_PATHS.VIDEOS()).value;
    }

    _initNoImgValidation(): ValidatorFn {
      const re = /<img\b[^>]*\bsrc\s*=\s*\\?"data:image/i;

      return (): ValidationErrors | null => {
        const contentBodyControl = this.form.get(this.FORM_PATHS.BODY());
        if (!contentBodyControl || !contentBodyControl.value) {
          return null;
        }

        let errors: ValidationErrors = {};
        if (re.test(contentBodyControl.value)) {
          errors[FORM_FIELDS.BODY] = true;
          contentBodyControl.setErrors(errors);
        }

        return Object.keys(errors).length === 0 ? null : errors;
      };
    }
  };
}
